import { useMemo } from 'react';
import { DrawerLayout } from 'components/DrawerLayout/DrawerLayout';
import { fieldMappingSelector } from 'slices/field.slice';
import {
  reservationTemplatesSelector,
  selectedReservationTemplateMap,
} from 'slices/reservationTemplate.slice';
import { useAppSelector } from 'slices/store';
import type { SyncSettingResponse } from '@timeedit/calendar-integration-api-contract';
import { TEDrawerSection } from '@timeedit/ui-components';
import { Form, type FormInstance } from '@timeedit/ui-components/antd';
import { MappingSelect } from './../mapping/MappingSelect/MappingSelect';

export const intlPrefix = 'integration.calendar.sync_settings';

type EventReservationMapperProps = {
  form: FormInstance<SyncSettingResponse>;
  label: string;
};

const EventReservationMapper = ({
  form,
  label,
}: EventReservationMapperProps) => {
  const selectableReservationTemplates = useAppSelector(
    reservationTemplatesSelector
  );
  const reservationTemplateMap = useAppSelector(selectedReservationTemplateMap);
  const fieldsMap = useAppSelector(fieldMappingSelector);

  const reservationTemplateField = Form.useWatch(
    ['syncMode', 'reservationTemplateId'],
    form
  );
  const fieldsOnTemplate = useMemo(() => {
    const template = reservationTemplateMap[reservationTemplateField];

    if (!template) {
      return [];
    }

    return template.fields
      .map((field) => {
        const fieldInfo = fieldsMap[field];

        if (!fieldInfo) {
          return null;
        }

        return {
          label: fieldInfo.name,
          value: fieldInfo.id,
          extId: fieldInfo.extId,
        };
      })
      .filter(Boolean);
  }, [reservationTemplateField, reservationTemplateMap, fieldsMap]);

  const templateOption = useMemo(() => {
    return selectableReservationTemplates.map((template) => ({
      label: template.name,
      value: template.id,
    }));
  }, [selectableReservationTemplates]);

  return (
    <TEDrawerSection label={label}>
      <DrawerLayout numberOfColumns={1}>
        <MappingSelect
          label="Reservation template"
          size="small"
          name={['syncMode', 'reservationTemplateId']}
          options={templateOption}
        />
        <MappingSelect
          label="Subject"
          size="small"
          name={['syncMode', 'subjectFieldId']}
          options={fieldsOnTemplate}
          disabled={!reservationTemplateField}
        />
        <MappingSelect
          label="Organiser"
          size="small"
          name={['syncMode', 'organiserFieldId']}
          options={fieldsOnTemplate}
          disabled={!reservationTemplateField}
        />
      </DrawerLayout>
    </TEDrawerSection>
  );
};

export default EventReservationMapper;
